import React, { Fragment } from 'react';
import { DragHandle } from '@components/drag-handle';
import { useDragToClose } from '@components/modals/modal-v2/src/use-drag-to-close';
import { Dialog, DialogPanel, Transition, TransitionChild } from '@headlessui/react';
import classNames from 'classnames';

import { Props } from './modal.types';

export const Modal = ({
  hasMobileHandle = true,
  enableDragToClose = true,
  isOpen,
  handleClose,
  variant = 'lg',
  children,
  maxWidthClass,
  beforeEnter,
  afterEnter,
  beforeLeave,
  afterLeave,
  enter = '',
  borderRadiusContainerClassName = 'rounded-t-2xl sm:rounded-2xl',
  contentClassName = 'sm:border sm:border-extra-soft',
}: Props) => {
  const { panelRef, isClosing, handleTouchStart, handleTouchMove, handleTouchEnd, handleTouchCancel, initiateClose } =
    useDragToClose({ isOpen, onClose: handleClose, enabled: enableDragToClose });

  return (
    <Transition
      enter={enter}
      show={isOpen && !isClosing}
      as={Fragment}
      appear={true}
      beforeEnter={beforeEnter}
      afterEnter={afterEnter}
      beforeLeave={beforeLeave}
      afterLeave={afterLeave}>
      <Dialog className='relative z-40' onClose={() => initiateClose('backdrop')} aria-label='modal'>
        <TransitionChild
          enter='duration-[450ms] ease-[cubic-bezier(0.32,0.72,0,1)]'
          enterFrom='opacity-0'
          enterTo='opacity-100'
          leave='duration-[350ms] ease-[cubic-bezier(0.32,0.72,0,1)]'
          leaveFrom='opacity-100'
          leaveTo='opacity-0'>
          <div className='fixed inset-0 backdrop-overlay' aria-hidden='true' />
        </TransitionChild>
        <TransitionChild
          as={Fragment}
          enter='duration-[450ms] ease-[cubic-bezier(0.32,0.72,0,1)]'
          enterFrom='opacity-0 max-sm:opacity-100 max-sm:translate-y-full translate-y-4 sm:translate-y-0 sm:scale-95'
          enterTo='opacity-100 translate-y-0 sm:scale-100'
          leave='duration-[350ms] ease-[cubic-bezier(0.32,0.72,0,1)]'
          leaveFrom='opacity-100 translate-y-0 sm:scale-100'
          leaveTo='opacity-0 max-sm:opacity-100 max-sm:translate-y-full translate-y-4 sm:translate-y-0 sm:scale-95'>
          <div className='fixed inset-0 flex items-end w-screen sm:items-center sm:justify-center'>
            <DialogPanel
              ref={panelRef}
              className={classNames(
                'bg-primary relative w-full sm:w-10/12 h-auto max-h-[95dvh] overflow-hidden',
                'sm:align-middle lg:my-8',
                'shadow-[2px_2px_20px_0_rgba(0,0,0,0.15)]',
                'overscroll-contain',
                borderRadiusContainerClassName,
                contentClassName,
                {
                  'lg:max-w-211.5': variant === 'lg' && !maxWidthClass,
                  'lg:max-w-(--breakpoint-md)': variant === 'md' && !maxWidthClass,
                  'lg:max-w-(--breakpoint-sm)': variant === 'sm' && !maxWidthClass,
                  [`${maxWidthClass}`]: !!maxWidthClass,
                }
              )}>
              <div className='max-h-[95dvh] overflow-auto scrollbar scrollbar-hide'>
                {hasMobileHandle && (
                  <DragHandle
                    className='sm:hidden'
                    onClose={() => initiateClose('drag')}
                    onTouchStart={handleTouchStart}
                    onTouchMove={handleTouchMove}
                    onTouchEnd={handleTouchEnd}
                    onTouchCancel={handleTouchCancel}
                  />
                )}
                {children}
              </div>
            </DialogPanel>
          </div>
        </TransitionChild>
      </Dialog>
    </Transition>
  );
};
